import SkeletonTransDetail from "@/components/skeleton/TransDetail";
import API from "@/constant/API";
import {
  CARD_REFUND_STATUS,
  CARD_TRANS_REFUND_STATUS,
  CARD_TRANS_STATUS,
  CARD_TRANSACTION_SHOW_STATUS
} from "@/constant/Constant";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader";
import LabelValue from "@/pages/components/LabelValue";
import RecordHeader from "@/pages/components/RecordHeader";
import { getCardTransStatusColor, getCardTransStatusColorV2, _getStatusV2 } from "@/utils/AssetsUtils";
import axiosInstance from "@/utils/axios";
import { toDateTimeString } from "@/utils/dateUtils";
import {getResourceByMcc, getResourceByMccName, getResourceByMccV2, getUrlParams} from "@/utils/utils";
import { Box, Stack, styled, Typography, useTheme, Link } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TransPaymentInfo from "./TransPaymentInfo";
import RpIcon from "@/components/RpIcon";
import { CARD_TRANSACTION_TYPE } from '@/constant/Constant'
import firebase from "firebase/compat";

import useSettings from "@/hook/useSettings";
interface Props {
  open: boolean;
  onClose: () => void;
  transactionId: string;
  cardId: string;
}

const StyledContainer = styled(Box)(({theme}) => ({
  padding: '16px'
}))
const StyledDetailTitle = styled(Typography)(({theme}) => ({
  fontSize: '16px',
  fontWeight: 700,
  color: theme.palette.text.primary,
  padding: '12px 0',
}))

export default function CardTransDetail() {
  const {t} = useTranslation("translation");
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState({})
  const [cardId, setCardId] = useState(null);
  const [cardType, setCardType] = useState(null);
  const [tid, setTid] = useState(null);
  const theme = useTheme();
  const { settings } = useSettings()

  const specialMccRuleLink = useMemo(() => {
    let _url = ''
    switch (settings.lang) {
      case 'hk':
        _url = `https://support.redotpay.com/hc/zh-hk/articles/11186408582031-%E7%89%B9%E5%88%AB%E5%95%86%E6%88%B7%E4%BA%A4%E6%98%93%E6%94%B6%E8%B4%B9%E8%A7%84%E5%88%99`
        break
      case 'ar':
        _url = `https://support.redotpay.com/hc/ar/articles/11186408582031-%E7%89%B9%E5%88%AB%E5%95%86%E6%88%B7%E4%BA%A4%E6%98%93%E6%94%B6%E8%B4%B9%E8%A7%84%E5%88%99`
        break
      default:
        _url = `https://support.redotpay.com/hc/en-us/articles/11186408582031-%E7%89%B9%E5%88%AB%E5%95%86%E6%88%B7%E4%BA%A4%E6%98%93%E6%94%B6%E8%B4%B9%E8%A7%84%E5%88%99`
        break
    }
    return _url
  }, [settings])

  useEffect(() => {
    const cardId = getUrlParams('cardId');
    const tid = getUrlParams('transactionId');
    const cardType = getUrlParams('cardType');
    setCardId(cardId);
    setTid(tid);
    setCardType(cardType)
  }, []);

  useEffect(() => {
    if (cardId != null && tid != null) {
      _loadData();
    }
  }, [cardId, tid]);

  const _loadData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(API.CARD_TRANS_DETAIL, {
        cardId, 
        transactionId: tid
      });
      setItem(res);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e.message, {variant: "error"})
    } finally {
      setLoading(false)
    }
  }

  const _getStatus = () => {
    if (item.hasRefund == 1 || item.isRefund == 1) {
      return <span className="text-primary-main">{t('refund')}</span>;
    }
    if (item.status == CARD_TRANS_STATUS.CLEARED) {
      return <span className="text-success-main">{t('card_trans_status_cleared')}</span>;
    } else if (item.status == CARD_TRANS_STATUS.DECLINED) {
      return <span className="text-primary-main">{t('card_trans_status_declined')}</span>;
    } else {
      return <span
        className="text-text-secondary dark:text-text-dark-secondary">{t('card_trans_status_pending')}</span>;
    }
  }

  const icon = useMemo(() => {
    if (!item) return null;

    let src, isMerchantIcon = false;

    src = getResourceByMccName(item.merchantName);
    isMerchantIcon = !!src;
    if (!src) {
      src = getResourceByMcc(item.mcc);
    }
    return {
      src, isMerchantIcon,
    }
  }, [item]);

  const _getAmount = () => {
    return `${item.isRefund !== 1 ? "-" : ""}${item.transactionAmount}`;
  }

  const getRefundStatus = () => {

    if (item.refundStatus == CARD_TRANS_REFUND_STATUS.PENDING) {
      return t('card_trans_status_refund_pending');
    } else if (item.refundStatus == CARD_TRANS_REFUND_STATUS.SUCCESS) {
      return t('card_trans_status_refund_success');
    } else if (item.refundStatus == CARD_TRANS_REFUND_STATUS.FAIL) {
      return t('card_trans_status_refund_fail');
    } else if (item.refundStatus == CARD_TRANS_REFUND_STATUS.CANCEL) {
      return t('card_trans_status_refund_cancel');
    } else {
      return t('card_trans_status_refund_wait');
    }
  }

  const _getStatusDisplay = () => {
    // 转账类型( isTransfer ==true)
    if (item.isTransfer) {
      if (item.isRefund == 1) {
        return t('card_trans_detail_status_refund');
      }
      return t('received')
    }

    // 退款类型 isTransfer=false
    if (item.isRefund == 1 || item.hasRefund == 1) {
      return getRefundStatus();
    }
    // 交易类型 (isTransfer==false 且 isRefund==0 且hasRefund==0)
    if (item.status == CARD_TRANS_STATUS.CLEARED) {
      return t('card_trans_status_cleared');
    } else if (item.status == CARD_TRANS_STATUS.DECLINED) {
      return t('card_trans_status_declined');
    } else if (item.status == CARD_TRANS_STATUS.CLEARED) {
      return t('card_trans_status_canceled');
    } else if (item.status == CARD_TRANS_STATUS.REFUND) {
      return t('card_trans_status_refund');
    } else {
      return t('card_trans_status_pending');
    }
  }

  const _getCardTransStatusIcon = () => {
    if (item.transactionType == CARD_TRANSACTION_TYPE.VISA_TRANSFER) {
      if (item.refundStatus == CARD_REFUND_STATUS.PENDING) {
        return 'check-circle'
      }
      if (item.refundStatus == CARD_REFUND_STATUS.SUCCESS) {
        return 'check-circle'
      }
      if (item.refundStatus == CARD_REFUND_STATUS.FAIL) {
        return 'close'
      }
    } else if (item.transactionType == CARD_TRANSACTION_TYPE.REFUND) {
      if (item.refundStatus == CARD_REFUND_STATUS.PENDING) {
        return 'clock';
      } else if (item.refundStatus == CARD_REFUND_STATUS.FAIL) {
        return 'close-circle';
      } else if (item.refundStatus == CARD_REFUND_STATUS.SUCCESS) {
        return 'check-circle';
      } else {
        return 'more';
      }
    } else {
      if (item.status == CARD_TRANS_STATUS.CLEARED) {
        return 'check-circle';
      } else if (item.status == CARD_TRANS_STATUS.DECLINED) {
        return 'close-circle';
      } else if (item.status == CARD_TRANS_STATUS.VOID) {
        return 'cancel';
      } else if (item.status == CARD_TRANS_STATUS.PENDING) {
        return 'check-circle';
      } else {
        return 'more';
      }
    }
  }

  const _getCardTransStatusIconV2 = () => {
    if (item.transactionShowStatus == CARD_TRANSACTION_SHOW_STATUS.CLEARED) {
      return 'check-circle';
    } else if (item.status == CARD_TRANSACTION_SHOW_STATUS.DECLINED) {
      return 'close-circle';
    } else {
      return 'more';
    }
  }

  const _getContent = () => {

    let icon = getResourceByMccV2( item.mccCode, item.merchantName);
      // getResourceByMccName(item.merchantName);
    let hasMerchantIcon = !!icon;
    let iconWidget = null;
    // if (!icon) {
    //   icon = getResourceByMcc(item.mccCode);
    // }
    if (!icon) {
      iconWidget = <RpIcon color={theme.palette.text.primary} icon="card" size={16} />;
    }

    const isVisaTransfer = item.transactionType == CARD_TRANSACTION_TYPE.VISA_TRANSFER;

    if (isVisaTransfer) {

    }

    return <Stack>
      <StyledContainer>
        <RecordHeader icon={icon} iconFilter={!hasMerchantIcon} iconWidget={iconWidget} iconTitle={item.merchantName} 
          statusColor={getCardTransStatusColorV2(item)} statusIcon={_getCardTransStatusIcon()} statusDisplay={_getStatusV2(item, t)}
          amount={_getAmount()} currencyCode={item.transactionCurrencyCode ?? item.transactionCurrency} isDeclined={item.transactionShowStatus === CARD_TRANSACTION_SHOW_STATUS.DECLINED}
                      isVisaTransfer={isVisaTransfer} isRefund={item.transactionType == CARD_TRANSACTION_TYPE.REFUND}
        />

        <TransPaymentInfo record={item} cardType={cardType} />
      </StyledContainer>

      <Box sx={{ width: '100%', height: '6px', backgroundColor: '#F3F5F9' }} />

      <StyledContainer>
        <StyledDetailTitle>{t('transaction_details')}</StyledDetailTitle>

        <Stack spacing={2}>
          <LabelValue label={t("transaction_id")} value={item.transactionId} copyEnable={true} />
          <LabelValue label={t("card_number")} value={`\u2022\u2022\u2022\u2022 ${item.last4}`} />
          <LabelValue label={t("mcc")} value={item.mccCategory} />
          {item.specialMcc && <Box
          sx={{
            marginTop: '0 !important',
            display: 'flex'
          }}
          ><Link sx={{
            width: 'auto',
            fontSize: '12px',
            display: 'inline-block',
            padding: '2px 2px',
            borderRadius: '6px',
            border:  `1px solid ${theme.palette.warning.main}`,
            color: theme.palette.warning.main,
            textDecoration: 'none',
          }} href={specialMccRuleLink} target={'_blank'}>
            <span>{t('special_mcc_fee')}</span>
            <RpIcon color={theme.palette.warning.main} size={12} icon="arrow-right-simple" />
          </Link></Box>}
          {item.channel && <LabelValue label={t("channel")} value={item.channel} />}
          {item.merchantCity && <LabelValue label={t("merchant_city")} value={item.merchantCity} />}
          {item.merchantCountry && <LabelValue label={t("merchant_country")} value={item.merchantCountry} />}
          <LabelValue label={t("transaction_date")} value={toDateTimeString(item.createTime)} />
        </Stack>
      </StyledContainer>
    </Stack>
  }

  return <Box sx={{ width: '100%' }}> 
    <SimpleHeader title={t('transaction_details')} />

    <Box sx={{ width: '100%' }}>
      {loading ? <StyledContainer>
        <SkeletonTransDetail itemCount={7} />
      </StyledContainer> : _getContent()}
    </Box>
  </Box>
}
